import { type ReactElement } from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { SUPPORTED_LOCALES_OBJ } from '@zep-us/i18n';
import { type GetServerSidePropsContext } from 'next';
import { type NextPageWithLayout } from 'next';
import { getBaseUrlWithLocale } from '@/consts/env';
import { getProductSidebarLayout } from '@/layout/ProductSidebarLayout';
import { isZepQuizApiError } from '@/shared/error/isZepQuizApiError';
import { type Folder, useFolderDetail } from '@/shared/features/quiz-folder';
import { FolderErrorFallback } from '@/shared/features/quiz-folder/components/FolderErrorFallback';
import { serverLogger } from '@/shared/logger/serverLogger';
import { withSsrLogging } from '@/shared/logger/withSsrLogging';
import { getDefaultServerSideProps } from '@/utils/next';
import { createTranslationAtSSR, setNotFoundPageCache, setPublicPageCache } from '@/utils/ssr';
import { makeFolderDetailSeoInfo } from './utils/makeFolderDetailSeoInfo';
import { FolderDetailPageView } from './FolderDetailPageView';

/**
 * `/folder/[id]` — 폴더 공개 상세.
 *
 * 공개 페이지라 `withPageAuth` 를 걸지 않는다. 크롤러·미리보기 봇이 빈 껍데기를 받지 않도록
 * `NoSSR` 도 쓰지 않고, 상세를 서버에서 미리 받아 `dehydratedState` 로 넘긴다.
 */
const FolderDetail: NextPageWithLayout<{
  folderId: string;
  initialLoadError?: boolean;
}> = ({
  folderId,
  initialLoadError
}) => initialLoadError ? <FolderErrorFallback actionHref={'/public'} /> : <FolderDetailPageView id={folderId} />;
FolderDetail.getLayout = (page: ReactElement) => getProductSidebarLayout(page, 'p-0', {
  hideMobileBottomNavigation: true,
  hideContentPaddingWithoutSidebar: true
});
export default FolderDetail;

/**
 * SSR 조회 실패의 세 갈래.
 *
 *  - 잘못된 형식·없는 id → 404
 *  - 공개·비공개 폴더의 id → 200
 *  - 예상 밖의 인증 오류(401·403) → 비캐시 응답에서 클라이언트 재조회
 *
 * 서버의 폴더 상세 계약은 비공개 폴더도 URL 조회를 허용한다.
 * HTTP 오류 본문의 `FAILED` 는 403·500 에도 쓰이므로 404 판정에 사용하지 않는다.
 */
type FetchErrorKind = 'not-found' | 'forbidden' | 'fatal';
type FolderFetchResult = {
  ok: true;
  folder: Folder;
} | {
  ok: false;
  kind: FetchErrorKind;
};
const classifyFetchError = (e: unknown): FetchErrorKind => {
  // `httpClient` 는 본문이 있는 HTTP 오류를 전부 `ZepQuizApiError` 로 감싼다. 남는 것(네트워크·타임아웃·본문 없는
  // 게이트웨이 오류)은 상태 코드가 없어 아래에서 `fatal` 이 된다 — 그 경우를 404 로 캐싱하면 안 된다.
  const status = isZepQuizApiError(e) ? e.httpStatus : undefined;
  if (status === 404) return 'not-found';
  if (status === 403 || status === 401) return 'forbidden';
  // 5xx·timeout·네트워크는 폴더 전용 오류 화면과 HTTP 500으로 응답한다.
  // 장애를 404로 캐싱하면 복구 후에도 계속 없는 페이지가 된다.
  return 'fatal';
};
export const getServerSideProps = withSsrLogging('folder-detail', async function getServerSidePropsInner(context: GetServerSidePropsContext) {
  const {
    locale = SUPPORTED_LOCALES_OBJ.en,
    params
  } = context;
  const folderId = params?.id as string ?? '';
  const isBot = context.req.headers['x-client-type'] === 'bot';
  const handleNotFound = () => {
    setNotFoundPageCache(context, {
      profile: isBot ? 'bot' : 'user'
    });
    return {
      notFound: true as const
    };
  };

  // 서버는 Mongo ObjectId 만 받는다. 잘못된 경로는 API 400·SSR 500 으로 보내지 않는다.
  if (!/^[0-9a-f]{24}$/i.test(folderId)) return handleNotFound();
  const queryClient = new QueryClient();
  const folderFetch: Promise<FolderFetchResult> = queryClient.fetchQuery(useFolderDetail.getFetchOptions({
    id: folderId
  })).then(folder => ({
    ok: true as const,
    folder
  })).catch((e: unknown) => ({
    ok: false as const,
    kind: classifyFetchError(e)
  }));
  const [{
    props: defaultProps
  }, fetched] = await Promise.all([
  // `classroom` 은 내 폴더의 퀴즈 추가 모달 검색창(`ContentSearchField`)이 쓰는 문구다.
  getDefaultServerSideProps({
    ...context,
    namespaces: ['common', 'classroom']
  }), folderFetch]);
  if (!fetched.ok) {
    // 공통 SSR 로그는 비공개(403)를 정상 응답과 같은 `props` 로 남긴다. 조회 결과를 따로 적는다.
    serverLogger.info({
      folder_id: folderId,
      folder_fetch: fetched.kind
    }, 'folder-detail ssr fetch rejected');
    if (fetched.kind === 'not-found') return handleNotFound();
    if (fetched.kind === 'fatal') {
      context.res.statusCode = 500;
      setPublicPageCache(context, {
        cacheable: false,
        profile: 'user'
      });
      return {
        props: {
          ...defaultProps,
          folderId,
          initialLoadError: true,
          noIndex: true
        }
      };
    }

    // 예상 밖의 인증 오류 — 사용자별 권한이 다를 수 있어 클라이언트가 토큰으로 재조회한다.
    // 데이터 없이 껍데기만 내려보낸다.
    // 캐시는 절대 하지 않는다(사용자별 응답이 섞이면 안 된다).
    setPublicPageCache(context, {
      cacheable: false,
      profile: 'user'
    });
    return {
      props: {
        ...defaultProps,
        folderId,
        noIndex: true
      }
    };
  }
  const {
    folder
  } = fetched;
  const canonicalUrl = `${getBaseUrlWithLocale(locale)}/folder/${folderId}`;

  // 엣지 캐시는 공개 폴더의 봇 요청에만 연다. 비공개는 사용자별 응답이 섞이지 않게 no-store 다.
  // 사람 요청도 캐시하지 않는다 — 폴더 목록이 HTML 에 들어 있어, 담거나 지운 직후 새로고침·링크 진입에서
  // 엣지가 옛 목록을 돌려줬다(라이브 `x-cache: Hit`, 7분 전 스냅샷). 폴더는 noindex 라 사람 쪽 캐시의
  // 실익도 없다. 봇은 캐시 키(`x-client-type`)가 갈려 사람 응답과 섞이지 않는다.
  const isPublic = folder.visibility === 'PUBLIC';
  setPublicPageCache(context, {
    cacheable: isPublic && isBot,
    profile: isBot ? 'bot' : 'user'
  });
  context.res.setHeader('Vary', 'x-client-type');
  // `Last-Modified`·304 를 쓰지 않는다. 폴더 `updatedAt` 은 담긴 퀴즈의 제목·썸네일이 바뀌어도 그대로라
  // 304 로 옛 HTML 을 계속 돌려준다. 담긴 퀴즈 응답에는 수정 시각이 없어 대신 쓸 값도 없다.
  // 폴더는 색인하지 않으므로 크롤 절약 효과도 없다.

  return {
    props: {
      ...defaultProps,
      folderId,
      dehydratedState: dehydrate(queryClient),
      seoContent: makeFolderDetailSeoInfo(folder, createTranslationAtSSR(defaultProps._nextI18Next, locale)),
      // 폴더는 아직 sitemap 에 없다(`next-sitemap.config.mjs` 가 폴더 엔드포인트를 부르지 않는다). sitemap 없이
      // 색인을 열면 크롤 예산만 쓰므로 배선 전까지 noindex 다. 메타·OG·canonical 은 공유 링크 미리보기에 필요해 둔다.
      noIndex: true,
      canonicalUrl,
      ogUrl: canonicalUrl
    }
  };
});